import { getNangoProviderLabel } from '@/features/integrations-connected-apps/nango/capabilities';
import { useMutateNangoConnectSession } from '@/features/integrations-connected-apps/nango/hooks/useMutateNangoConnectSession';
import { openNangoConnectUI } from '@/features/integrations-connected-apps/nango/open-nango-connect';
import { Button } from '@heidi/design-system/button';
import { toast } from '@heidi/design-system/toast';
import { NANGO_CONNECTIONS_QUERY_KEY } from '@heidi/integrations/connected-apps/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { Plus } from 'lucide-react';
import { type ComponentProps, type ReactNode, useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import type { NangoConnectSessionRequest } from '../types';

interface NangoConnectButtonProps {
  allowedIntegrations?: string[];
  /** Shorter “Connect” label for catalog rows. */
  compact?: boolean;
  /** Overrides compact/`undefined` so marketplace details can use `lg`. */
  size?: ComponentProps<typeof Button>['size'];
  testId?: string;
  disabled?: boolean;
  /** Fired after Nango Connect succeeds (in-chat connect chips). */
  onConnected?: () => void;
  /** Fired when session creation or the Connect UI errors. */
  onFailed?: () => void;
  /** True while the Connect UI is opening or the clinician is in OAuth. */
  onBusyChange?: (busy: boolean) => void;
  /** Replaces the default Connect copy (and hides the Plus). */
  children?: ReactNode;
}

export function NangoConnectButton({
  allowedIntegrations,
  compact = false,
  size,
  testId = 'nango-connect-button',
  disabled = false,
  onConnected,
  onFailed,
  onBusyChange,
  children,
}: NangoConnectButtonProps) {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const createSession = useMutateNangoConnectSession();
  const [isOpening, setIsOpening] = useState(false);

  const singleIntegration =
    allowedIntegrations?.length === 1 ? allowedIntegrations[0] : undefined;

  const handleConnect = useCallback(async () => {
    setIsOpening(true);
    onBusyChange?.(true);
    try {
      const body: NangoConnectSessionRequest = {};
      if (allowedIntegrations?.length) {
        body.allowed_integrations = allowedIntegrations;
      }
      const { session_token } = await createSession.mutateAsync(body);
      const result = await openNangoConnectUI({ sessionToken: session_token });
      if (result === 'connected') {
        await queryClient.invalidateQueries({ queryKey: [NANGO_CONNECTIONS_QUERY_KEY] });
        toast({
          tone: 'success',
          title: intl.formatMessage({
            defaultMessage: 'Connected',
            id: 'IvjoDSPGEQ',
          }),
          description: singleIntegration
            ? intl.formatMessage(
                {
                  defaultMessage: '{app} was connected successfully.',
                  id: 'Owh7ansf8I',
                },
                { app: getNangoProviderLabel(singleIntegration) }
              )
            : intl.formatMessage({
                defaultMessage: 'Your app was connected successfully.',
                id: 'P3TWFP3MRL',
              }),
        });
        onConnected?.();
      }
    } catch (error) {
      const isMissingBackend =
        isAxiosError(error) &&
        (error.response?.status === 404 || error.response?.status === 501);
      toast({
        tone: 'destructive',
        title: intl.formatMessage({
          defaultMessage: 'Could not start connect',
          id: 'cQu0bfxLKd',
        }),
        description: isMissingBackend
          ? intl.formatMessage({
              defaultMessage: 'Nango backend endpoints are not available yet.',
              id: 'QqKF9/RQv5',
            })
          : intl.formatMessage({
              defaultMessage: 'Something went wrong. Try again.',
              id: '3kJE8Gegin',
            }),
      });
      onFailed?.();
    } finally {
      setIsOpening(false);
      onBusyChange?.(false);
    }
  }, [
    allowedIntegrations,
    createSession,
    intl,
    onConnected,
    onFailed,
    onBusyChange,
    queryClient,
    singleIntegration,
  ]);

  const resolvedSize = size ?? (compact ? 'sm' : undefined);
  const showPlus = children == null && !compact;

  return (
    <Button
      onClick={() => void handleConnect()}
      disabled={disabled || isOpening || createSession.isPending}
      data-testid={testId}
      variant={compact && children == null ? 'secondary' : undefined}
      size={resolvedSize}
    >
      {showPlus ? <Plus className="size-4" /> : null}
      {children ??
        (compact ? (
          <FormattedMessage defaultMessage="Connect" id="+vVZ/G11Zg" />
        ) : (
          <FormattedMessage defaultMessage="Connect an app" id="vY6tjXubz1" />
        ))}
    </Button>
  );
}
